"use client";

import React, { useState } from "react";
import Link from "next/link";
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon
} from "@heroicons/react/24/outline";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface ContactInfo {
  email: string;
  phone?: string;
  hours: string;
}

interface SupportResource {
  title: string;
  url: string;
  description: string;
}

interface SystemStatus {
  systemStatus: "operational" | "maintenance" | "degraded";
  lastUpdated: string;
  maintenanceWindow?: string | null;
  statusPage?: string;
}

export default function SupportPage() {
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Hardcoded support configurations
  const contact: ContactInfo = {
    email: "verify-support@mira.network",
    hours: "24/7 - Response within 2-3 business days"
  };

  const faq: FAQItem[] = [
    {
      id: "getting-started",
      question: "How do I get started with fact verification?",
      answer: "Simply navigate to the verification page, paste or type your statement, and click 'Verify'. Our AI models will analyze the claim and provide you with a consensus result.",
      category: "basics"
    },
    {
      id: "accuracy-rates",
      question: "How accurate is the verification system?",
      answer: "Our multi-model consensus approach typically achieves high accuracy rates by having multiple AI models cross-check each other's work. You can choose between 2-of-3 or 3-of-3 consensus for different confidence levels.",
      category: "accuracy"
    },
    {
      id: "rate-limits",
      question: "What are the rate limits for verification?",
      answer: "Users get 10,000 tokens per hour and 100,000 tokens per day by default. Rate limits may be adjusted over time. If you need higher limits, contact support to request an increase. Check your current usage in your dashboard.",
      category: "usage"
    },
    {
      id: "chrome-extension",
      question: "How do I use the Chrome extension?",
      answer: "Install the extension from the Chrome Web Store (ID: hihhifjnmddhenncjpfgikhdhmjbhleh), log in with your account, then select any text on a webpage and right-click to choose 'Verify with Mira' or use the extension popup.",
      category: "extension"
    },
    {
      id: "access-waitlist",
      question: "How do I get access to Mira Verify?",
      answer: "Mira Verify is currently in beta with access limited to approved users. You can join the waitlist from the /login page. We're gradually approving new users and will notify you when your access is ready.",
      category: "basics"
    },
    {
      id: "pricing-plans",
      question: "Do you have premium plans or pricing tiers?",
      answer: "We're currently in beta with free access for approved users. Users can request higher rate limits by contacting support. Premium subscription plans with additional features will be available soon. Join our Discord community to stay updated.",
      category: "usage"
    }
  ];

  const resources: Record<string, SupportResource> = {
    research: {
      title: "Research Paper",
      url: "https://arxiv.org/abs/2411.06535",
      description: "Read our academic research on multi-model verification"
    },
    discord: {
      title: "Discord Community",
      url: "https://discord.com/invite/mira-network",
      description: "Join our community for discussions and support"
    },
    website: {
      title: "Mira Network",
      url: "https://mira.network",
      description: "Learn more about the Mira ecosystem and our mission"
    }
  };

  const status: SystemStatus = {
    systemStatus: "operational",
    lastUpdated: new Date().toISOString(),
    maintenanceWindow: null,
    statusPage: "https://verify.mira.network"
  };

  // Get unique categories for filtering
  const categories = ["all", ...Array.from(new Set(faq.map(item => item.category)))];
  const filteredFAQ = selectedCategory === "all" ? faq : faq.filter(item => item.category === selectedCategory);

  const getStatusIcon = (systemStatus: string) => {
    switch (systemStatus) {
      case "operational":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "degraded":
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case "maintenance":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <QuestionMarkCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <img src="/mira-logo.svg" alt="Mira Logo" className="h-6 w-auto brightness-0" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Support Center</h1>
              <p className="text-sm text-gray-400">Get help with Mira Verify</p>
            </div>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            How can we help you?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Find answers to common questions and get support for our fact verification platform
          </p>
          
          {/* Quick Contact */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-6 w-6 text-blue-400" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-300">Email Support</p>
                  <a href={`mailto:${contact.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">
                    {contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ClockIcon className="h-6 w-6 text-green-400" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-300">Response Time</p>
                  <p className="text-sm text-gray-400">{contact.hours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-12">
            <div className="flex items-center justify-center gap-3">
              {getStatusIcon(status.systemStatus)}
              <div className="text-left">
                <p className="text-sm font-medium text-green-400">
                  System {status.systemStatus.charAt(0).toUpperCase() + status.systemStatus.slice(1)}
                </p>
                <p className="text-xs text-gray-400">
                  Updated {new Date(status.lastUpdated).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          
          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? "bg-white text-black"
                      : "bg-white/10 text-gray-300 hover:bg-white/20"
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQ.map((item) => (
              <div key={item.id} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === item.id ? null : item.id)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/10 transition-colors"
                >
                  <span className="font-medium text-white">{item.question}</span>
                  <div className="flex-shrink-0 ml-4">
                    {expandedFAQ === item.id ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
                {expandedFAQ === item.id && (
                  <div className="px-6 pb-4 bg-white/5">
                    <p className="text-gray-300 leading-relaxed">{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Additional Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(resources).map(([key, resource]) => (
              <Link
                key={key}
                href={resource.url}
                target={resource.url.startsWith('http') ? '_blank' : '_self'}
                rel={resource.url.startsWith('http') ? 'noopener noreferrer' : ''}
                className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300"
              >
                <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-gray-200">
                  {resource.title}
                </h3>
                <p className="text-gray-400 mb-4">
                  {resource.description}
                </p>
                <div className="flex items-center text-sm text-blue-400 group-hover:text-blue-300">
                  <span>Learn more</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Brand Section */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <img src="/mira-logo.svg" alt="Mira Logo" className="h-5 w-auto brightness-0" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Mira Verify</h3>
                  <p className="text-sm text-gray-400">AI-powered fact verification</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Advanced multi-model consensus verification system that helps you distinguish fact from fiction.
              </p>
            </div>
            
            {/* Platform Links */}
            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link href="/" className="text-gray-400 hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link href="/app" className="text-gray-400 hover:text-white transition-colors">
                    Verify Claims
                  </Link>
                </li>
                <li>
                  <Link href="/app/analysis" className="text-gray-400 hover:text-white transition-colors">
                    Intelligence Dashboard
                  </Link>
                </li>
                <li>
                  <Link href="/login" className="text-gray-400 hover:text-white transition-colors">
                    Login
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Support Links */}
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link href="/support" className="text-gray-400 hover:text-white transition-colors">
                    Help Center
                  </Link>
                </li>
                <li>
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Contact Support
                  </a>
                </li>
                <li>
                  <Link href="/privacy" className="text-gray-400 hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link 
                    href="https://discord.com/invite/mira-network"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Discord Community
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Section */}
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400">
            <div className="mb-4 sm:mb-0">
              © 2024 Mira Network. All rights reserved.
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>System Operational</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}